<template>
  <v-text-field :label="label" :class="clazz" :model-value="value" :readonly="readonly"
                @update:model-value="onInput" ref="textField" :key="aliasKey" @blur="onBlur" :error-messages="errorMessages" :density="dense ? 'compact' : undefined"
                :hide-details="hideDetails" :hint="hint" :persistent-hint="!!hint"
                :append-inner-icon="manual && !readonly ? 'mdi-refresh-auto' : undefined"
                @click:append-inner="onChangeToAuto">
    <!-- O nome de fórmula da dica (`em fórmulas: \`members\``) na fonte de código, como nas explicações
         (pedido do usuário). O erro passa igual: não tem crase -->
    <template #message="{message}"><ontos-code-text :text="message"/></template>
  </v-text-field>
</template>
<script lang="ts">
import {Component, Vue, Prop, Watch, toNative} from 'vue-facing-decorator'
import CodeText from '@/components/shared/CodeText.vue'

/**
 * Um campo que se preenche sozinho a partir de outro (`source`), até alguém digitar nele — aí fica
 * manual, e o ícone de recarregar dentro do campo o volta ao automático.
 *
 * O ícone DENTRO do campo (`append-inner`), e não um botão ao lado: ao lado, o botão era mais alto que o
 * campo sublinhado, o campo esticava até a altura dele e a linha descia, abrindo um vão entre o texto e
 * ela (relato do usuário). Compactar o botão não bastou.
 */
@Component({emits: ['input:manual', 'input', 'blur'], components: {'ontos-code-text': CodeText}})
class ComputedInput extends Vue {
  @Prop({type: String}) label!: string
  @Prop({required: true}) value!: any
  @Prop({required: true}) source!: any
  @Prop({type: Boolean, default: false}) readonly!: boolean
  @Prop({type: Boolean, default: false}) manual!: boolean
  @Prop({type: Function}) compute!: Function | undefined
  @Prop({type: Function}) computeOnInput!: Function | undefined
  @Prop({type: String}) errorMessages!: string
  @Prop({type: Boolean, default: false}) dense!: boolean
  /**
   * Repassado ao campo. Numa célula de tabela (o identificador de um papel, no `RolesDialog`) é
   * `'auto'`, como o nome ao lado: sem isso o campo reserva a linha de mensagem, e a linha da tabela
   * fica mais alta que as vizinhas.
   */
  @Prop({type: [Boolean, String], default: false}) hideDetails!: boolean | 'auto'
  /** Uma dica sempre à vista embaixo do campo — o nome em fórmulas, embaixo do identificador. */
  @Prop({type: String, default: ''}) hint!: string

  aliasKey = 0

  @Watch('source')
  onSourceChange(newSource: any) {
    if (!this.readonly && !this.manual) {
      this.valueModel = this.safeCompute(newSource)
    }
  }

  get clazz(): string {
    return this.readonly ? 'readonly' : ''
  }

  get manualModel(): boolean {
    return this.manual
  }

  set manualModel(value: boolean) {
    if (value !== this.manual) this.$emit('input:manual', value)
  }

  get valueModel(): any {
    return this.value
  }

  set valueModel(value: any) {
    if (value !== this.value) this.$emit('input', value)
  }

  //O v-text-field do Vuetify 4 não tem mais $refs.input: o que ele devolve é um proxy
  //que encaminha para refs internos. Procurar no DOM não depende desse interno.
  inputElement(): HTMLInputElement | undefined {
    return (this.$el as HTMLElement)?.querySelector('input') ?? undefined
  }

  /**
   * O campo está sendo remontado pelo `updateHack`. O navegador tira o foco do input que sai da tela, e
   * esse blur não é o de quem saiu do campo: tratado como tal, aplicava a sugestão completa no meio da
   * digitação, e o espaço que acabara de virar hífen perdia o hífen (relato do usuário).
   */
  remounting = false

  onBlur(event: Event) {
    if (this.remounting) return
    if (this.computeOnInput) this.$emit('input', this.safeCompute(this.value))
    this.$emit('blur', event)
  }

  safeCompute(source: any): any {
    return this.compute ? this.compute(source) : source
  }

  onInput(inputText: string) {
    this.manualModel = true
    if (!this.computeOnInput) {
      this.valueModel = inputText
    } else {
      const newAlias = this.computeOnInput(inputText)
      this.valueModel = newAlias
      this.updateHack(inputText, newAlias)
    }
  }

  onChangeToAuto() {
    this.manualModel = false
    this.valueModel = this.safeCompute(this.source)
  }

  updateHack(inputValue: string, desiredValue: string) {
    let selectionStart = this.inputElement()!.selectionStart!
    if (desiredValue !== inputValue) {
      if (desiredValue.length < inputValue.length) selectionStart = selectionStart - 1
      this.$nextTick(() => {
        this.remounting = true
        this.aliasKey++
        this.$nextTick(() => {
          this.remounting = false
          const updatedInput = this.inputElement()!
          updatedInput.focus()
          updatedInput.setSelectionRange(selectionStart, selectionStart)
        })
      })
    }
  }
}


export default toNative(ComputedInput)
</script>
